import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Toggle } from "@/components/shared/Toggle";
import { useAuth } from "@/context/AuthContext";

const SIGNIN_FIELDS = [
  { group: "Welcome panel (left side)", items: [
    ["hero_headline", "Big headline"], ["hero_subtext", "Paragraph"], ["hero_footer", "Footer line"],
  ]},
  { group: "Chooser screen", items: [
    ["headline", "Headline"], ["subtext", "Subtext"],
    ["google_button", "Google button label"], ["email_toggle", "Email option label"],
    ["footer_note", "Footer note"],
  ]},
  { group: "Email sign-in", items: [
    ["email_headline", "Heading"], ["email_subtext", "Subtext"],
    ["email_field_label", "Email field label"], ["password_field_label", "Password field label"],
    ["signin_submit", "Sign-in button"], ["forgot_link", "Forgot-password link"],
    ["to_register", "Link to create account"],
  ]},
  { group: "Create account", items: [
    ["register_headline", "Heading"], ["register_subtext", "Subtext"],
    ["name_field_label", "Name field label"], ["register_submit", "Create button"],
    ["to_signin", "Link back to sign in"],
  ]},
  { group: "Forgot & reset password", items: [
    ["forgot_headline", "Forgot heading"], ["forgot_subtext", "Forgot subtext"],
    ["forgot_submit", "Send-link button"], ["forgot_success", "Confirmation message"],
    ["reset_headline", "Reset heading"], ["reset_subtext", "Reset subtext"], ["reset_submit", "Reset button"],
  ]},
  { group: "Messages", items: [
    ["invalid_credentials", "Wrong email/password message"],
    ["account_exists_msg", "Email already exists message"],
    ["use_google_msg", "Use-Google-instead message"],
  ]},
];

export default function SignInScreen() {
  const { refetch } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/signin-config");
    setCfg(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return <div className="text-ink/55 py-8">Loading…</div>;

  const setField = (k, v) => setCfg({ ...cfg, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/signin-config", { value: cfg });
      toast.success("Sign-in screen updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    try {
      const r = await api.post("/signin-config/reset");
      setCfg(r.data);
      toast.success("Reset to default wording");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reset"); }
  };

  return (
    <div>
      <div className="bg-gold/15 border border-gold/40 rounded-lg p-4 mb-5 text-[15px] text-ink/80">
        Every word on the sign-in screen is editable here — nothing on that screen requires a code change to reword.
        You can also turn the email &amp; password option on or off for the whole system.
      </div>

      <div className="grid gap-5" style={{ gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr)" }}>
        <div className="space-y-5">
          <div className="bg-cream border border-ink/15 rounded-xl p-5 flex items-center justify-between gap-4" data-testid="email-password-toggle-row">
            <div>
              <div className="text-[16px] font-semibold">Allow email &amp; password sign-in</div>
              <div className="text-[14px] text-ink/60">Turn off to require Google sign-in only. Existing password accounts are kept.</div>
            </div>
            <Toggle testid="email-password-toggle" checked={cfg.email_password_enabled}
              onChange={() => setField("email_password_enabled", !cfg.email_password_enabled)} />
          </div>

          {SIGNIN_FIELDS.map((grp) => (
            <div key={grp.group} className="bg-cream border border-ink/15 rounded-xl p-5">
              <h3 className="font-zilla text-lg font-semibold mb-3">{grp.group}</h3>
              <div className="space-y-3">
                {grp.items.map(([key, label]) => (
                  <div key={key}>
                    <label className="text-[13px] font-semibold text-ink/70">{label}</label>
                    <input data-testid={`signin-field-${key}`} value={cfg[key] || ""}
                      onChange={(e) => setField(key, e.target.value)}
                      className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] mt-1" />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="flex gap-3 sticky bottom-0 bg-warm-cream py-3">
            <button data-testid="signin-save-button" onClick={save} disabled={saving}
              className="bg-maroon text-cream font-semibold rounded-lg px-6 py-3 min-h-[48px] text-[15px] disabled:opacity-50">Save changes</button>
            <button data-testid="signin-reset-button" onClick={resetDefaults}
              className="border border-ink/20 rounded-lg px-6 py-3 min-h-[48px] text-[15px] font-semibold">Reset to defaults</button>
          </div>
        </div>

        <div className="lg:sticky lg:top-4 self-start">
          <div className="text-[13px] uppercase tracking-widest text-ink/50 font-bold mb-2">Live preview</div>
          <div className="bg-cream border border-ink/15 rounded-2xl p-6 shadow-lg" data-testid="signin-preview">
            <div className="flex justify-center mb-4"><img src="/logo-full.png" alt="" className="h-12" /></div>
            <h2 className="font-zilla text-2xl font-semibold mb-1">{cfg.headline}</h2>
            <p className="text-ink/70 text-[15px] mb-4">{cfg.subtext}</p>
            <div className="bg-maroon text-cream font-semibold rounded-xl px-4 py-3 text-center text-[15px] mb-2">{cfg.google_button}</div>
            {cfg.email_password_enabled && (
              <div className="border-[1.5px] border-ink/25 rounded-xl px-4 py-3 text-center text-[15px] font-semibold">{cfg.email_toggle}</div>
            )}
            <p className="mt-4 pt-3 border-t border-ink/12 text-[13px] text-ink/60">{cfg.footer_note}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
